import { InputField } from '@advanza/input'
import Modal from '@advanza/modal'
import { Button, Divider, Icon, LinearLoader, LoadingDots, PreIcon } from '@advanza/ui'
import { useToast } from 'components/misc/Toaster'
import QualityScore from 'components/providers/QualityScore'
import { utcToLocal } from 'date'
import { useQualityScores } from 'hooks/providerHooks'
import { hasAccess, isSuperUser } from 'misc/user'
import { useState } from 'react'

interface QualityScoresProps {
    providerId: number
}

const QualityScores = ({ providerId }: QualityScoresProps) => {
    const {
        scores,
        recalcQualityScore,
        resetQualityScore,
        resetCooldown,
        statsStartDate,
        debugInfo,
        isReCalcing,
        isLoading,
    } = useQualityScores(providerId)

    const today = (() => {
        const d = new Date()
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
            d.getDate(),
        ).padStart(2, '0')}`
    })()
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isResetting, setIsResetting] = useState(false)
    const [resetSuccess, setResetSuccess] = useState(false)
    const [startDate, setStartDate] = useState(today)
    const { addToast } = useToast()

    const handleReset = () => {
        setIsResetting(true)
        resetQualityScore(startDate).then((result) => {
            setIsResetting(false)
            if (result.success) {
                setResetSuccess(true)
                setTimeout(() => setIsModalOpen(false), 1500)
                return
            }
            // Cooldown rejections are already surfaced in the modal via resetCooldown.
            if (result.error === 'quality_score_reset_cooldown') {
                return
            }
            addToast({
                msg: `Failed to reset reclaim score: ${result.error ?? 'unknown error'}`,
                red: true,
                icon: 'error',
            })
        })
    }

    const openModal = () => {
        setResetSuccess(false)
        setStartDate(today)
        setIsModalOpen(true)
    }

    const hasCooldown = resetCooldown !== null
    const canForceReset = isSuperUser() || Boolean(hasAccess('admin', 'w'))

    return (
        <div>
            <div className="flex items-center justify-end gap-3">
                <Button name="text" onClick={openModal}>
                    <PreIcon icon="restart_alt" red>
                        Reset Reclaim Score
                    </PreIcon>
                </Button>
                <Button name="text" onClick={() => recalcQualityScore(providerId)}>
                    {isReCalcing ? (
                        <span>
                            calculating
                            <LoadingDots />
                        </span>
                    ) : (
                        <PreIcon primColor icon="refresh">
                            Recalculate Quality Scores
                        </PreIcon>
                    )}
                </Button>
            </div>
            <Modal close={() => setIsModalOpen(false)} maxWidth={500} open={isModalOpen}>
                <div className="p-4">
                    <h3>Reset Reclaim Score</h3>
                    <Divider m />
                    {hasCooldown ? (
                        <>
                            <div className="flex items-center gap-2 text-amber-600">
                                <Icon name="schedule" />
                                <span>
                                    Cooldown active. Next reset allowed on{' '}
                                    {resetCooldown.next_allowed} ({resetCooldown.days_remaining}{' '}
                                    days remaining).
                                </span>
                            </div>
                            <Divider m />
                            {resetSuccess && (
                                <>
                                    <div className="flex items-center gap-2 text-green-500">
                                        <Icon name="check_circle" />
                                        <span>Reclaim score reset successfully.</span>
                                    </div>
                                    <Divider m />
                                </>
                            )}
                            {canForceReset && !resetSuccess && (
                                <>
                                    <label
                                        htmlFor="reclaim-stats-start-date-force"
                                        className="mb-1 block text-md font-medium text-neutral-700">
                                        Reclaim stats start date
                                    </label>
                                    <InputField
                                        id="reclaim-stats-start-date-force"
                                        type="date"
                                        value={startDate}
                                        max={today}
                                        onChange={(e) => setStartDate(e.target.value)}
                                    />
                                    <Divider m />
                                </>
                            )}
                            <div className="flex gap-2">
                                {canForceReset && (
                                    <Button
                                        disabled={isResetting || resetSuccess}
                                        onClick={handleReset}>
                                        {isResetting ? <LoadingDots /> : 'Force Reset'}
                                    </Button>
                                )}
                                <Button name="text" onClick={() => setIsModalOpen(false)}>
                                    Close
                                </Button>
                            </div>
                        </>
                    ) : (
                        <>
                            <p>
                                This resets the reclaim % by recalculating it only over leads from
                                the chosen start date onwards, giving the provider a clean slate in
                                search.
                            </p>
                            <Divider s />
                            <p className="text-md text-neutral-500">
                                A 90-day cooldown applies per provider.
                            </p>
                            <Divider m />
                            <label
                                htmlFor="reclaim-stats-start-date"
                                className="mb-1 block text-md font-medium text-neutral-700">
                                Reclaim stats start date
                            </label>
                            <InputField
                                id="reclaim-stats-start-date"
                                type="date"
                                value={startDate}
                                max={today}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                            <Divider m />
                            {resetSuccess && (
                                <>
                                    <div className="flex items-center gap-2 text-green-500">
                                        <Icon name="check_circle" />
                                        <span>Reclaim score reset successfully.</span>
                                    </div>
                                    <Divider m />
                                </>
                            )}
                            <div className="flex gap-2">
                                <Button
                                    disabled={isResetting || resetSuccess}
                                    onClick={handleReset}>
                                    {isResetting ? <LoadingDots /> : 'Confirm Reset'}
                                </Button>
                                <Button name="text" onClick={() => setIsModalOpen(false)}>
                                    Cancel
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </Modal>
            {isLoading && <LinearLoader />}
            {statsStartDate && (
                <div className="mx-1 my-4 flex items-center gap-2 rounded-md bg-blue-50 px-3 py-2 text-md text-blue-700">
                    <Icon name="info" />
                    <span>
                        Stats window starts from{' '}
                        <strong>{utcToLocal(statsStartDate, 'd MMM yyyy, HH:mm')}</strong>
                    </span>
                </div>
            )}
            {scores.map((score: { quality_score_id: number }, i: number) => (
                <div key={score.quality_score_id}>
                    <QualityScore
                        open={i === 0}
                        first={i === 0}
                        data={score}
                        dataCompare={scores[i + 1]}
                        debugInfo={debugInfo[score.quality_score_id]}
                    />
                    <Divider l={i === 0} />
                </div>
            ))}
        </div>
    )
}

export default QualityScores
